/** Content-free frontend transform loss, not a prediction of how a paste target renders. */
export type CopyFormattingLoss = {
  headings: boolean;
  boldAndItalics: boolean;
  bulletPoints: boolean;
  tables: boolean;
};

/**
 * Compare actual source and transformed HTML from the SAME copy attempt.
 * Designed for removal transforms: fewer semantic nodes means partial or total
 * structural loss, even when other nodes in the category survive. Emphasis is
 * compared by kind so surviving italics cannot hide stripped bold/underline;
 * equivalent b/strong and i/em tags are counted together. Tables are compared
 * as whole table elements: deleting rows/cells inside a retained table is content
 * deletion, not the copy setting's table-formatting removal.
 *
 * Each boolean reports which formatting was lost, not which toggle caused it.
 * One toggle can remove another category: `tables: false` flattens cells and
 * drops the headings and lists inside them, so `headings` / `bulletPoints` can
 * report loss while their own toggles are on.
 *
 * Only native HTML semantics count (no CSS or markdown/literal-dash inference).
 * The result contains exactly four booleans: no content, counts or DOM escapes.
 * Requires DOMParser at call time; importing this module is SSR-safe.
 */
export const detectCopyFormattingLoss = (
  sourceHtml: string,
  outputHtml: string
): CopyFormattingLoss => {
  if (typeof DOMParser === 'undefined') {
    throw new Error('Copy formatting-loss detection requires DOMParser');
  }
  const parser = new DOMParser();
  const source = parser.parseFromString(sourceHtml, 'text/html').body;
  const output = parser.parseFromString(outputHtml, 'text/html').body;
  const lost = (...selectors: string[]): boolean =>
    selectors.some(
      (selector) =>
        output.querySelectorAll(selector).length <
        source.querySelectorAll(selector).length
    );

  return {
    headings: lost('h1, h2, h3, h4, h5, h6'),
    boldAndItalics: lost('b, strong', 'i, em', 'u'),
    bulletPoints: lost('ul, ol', 'ul > li, ol > li'),
    tables: lost('table'),
  };
};
